import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, MessageSquareText, Globe, FileText, Check } from "lucide-react";

/* 站内法务页路径：从这些页面点「我知道了」不再回退到另一个法务页，直接回登录页 */
const LEGAL_PATHS = ["/terms", "/privacy", "/sms-terms"];

export const COMPANY = {
  brand: "ALIGCUS Construction",
  brandZh: "阿里建筑",
  legal: "Arcus Construction Inc. (dba ALIGCUS Construction)",
  address: "3637 San Gabriel River Pkwy, STE A, Pico Rivera, CA 90660",
  phone: "(626) 779-9127",
  email: "info@aligcus.com",
  privacyEmail: "peter@aligcus.com",
  site: "https://aliadu.com",
  updated: "September 24, 2026",
};

/* 「审核待确认」内联标记：仅用于尚无代码 / 文件依据、需公司确认后才能作为事实公开的表述 */
export function Pending({ zh, note, children }) {
  return (
    <span className="inline" data-testid="legal-pending" title={note || ""}>
      <span className="bg-amber-50 text-amber-900 border-b border-dashed border-amber-400">{children}</span>
      <sup className="ml-0.5 text-[9px] font-bold text-amber-600 whitespace-nowrap">[{zh ? "审核待确认" : "pending review"}]</sup>
    </span>
  );
}

/* 页面顶部审核横幅：列出全部待确认项；清空 items 后横幅自动消失 */
export function ReviewBanner({ zh, items = [] }) {
  if (!items.length) return null;
  return (
    <div className="mb-5 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-[13px] text-amber-900" data-testid="legal-review-banner">
      <p className="font-bold">{zh ? `预览草案 · 尚未生效 · ${items.length} 项事实待公司审核确认` : `Preview draft · not yet in force · ${items.length} statements pending company review`}</p>
      <p className="text-xs mt-1">{zh ? "以下表述目前没有代码或文件依据，页面内以「审核待确认」标出；确认或改写前请勿把本页作为正式政策发布或填入 Intuit 应用资料。" : "The statements below have no code or document evidence yet and are marked “pending review” in the text. Do not publish this page as the official policy or submit its URL to Intuit until they are confirmed or rewritten."}</p>
      <ol className="list-decimal pl-5 mt-2 space-y-0.5 text-xs">{items.map((t, i) => <li key={i}>{t}</li>)}</ol>
    </div>
  );
}

/* 公开法务页外壳（英文为主 + 中文切换；供运营商 / Twilio 审核直接访问） */
export function LegalPage({ title, titleZh, icon: Icon = ShieldCheck, testid, children }) {
  const [lang, setLang] = useState("en");
  const zh = lang === "zh";
  const [co, setCo] = useState(COMPANY);
  const navigate = useNavigate();
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/company-info`).then(({ data }) => setCo((c) => ({ ...c, ...Object.fromEntries(Object.entries(data).filter(([k, v]) => v && k in c)) }))).catch(() => {}); }, []);

  /* 底部「我知道了」出口：站内跳进来的回上一页；直接打开链接（无来源 / 来源是法务页 / 新标签无历史）则回登录页 */
  const dismiss = () => {
    let from = null;
    try { from = document.referrer ? new URL(document.referrer) : null; } catch { from = null; }
    const sameOrigin = !!from && from.origin === window.location.origin;
    const hasBack = window.history.length > 1;
    if (sameOrigin && !LEGAL_PATHS.includes(from.pathname) && hasBack) return navigate(-1);
    if (!from && hasBack) return navigate(-1);
    return navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f6f5f2] text-slate-800" data-testid={testid}>
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center gap-3">
          <Icon className="h-6 w-6 text-red-500 shrink-0" />
          <div className="min-w-0">
            <p className="font-bold text-base sm:text-lg leading-tight">{zh ? titleZh : title}</p>
            <p className="text-[11px] text-slate-300" data-testid="legal-brand">{co.brand} · {co.brandZh}</p>
          </div>
          <button type="button" data-testid="legal-lang-toggle" onClick={() => setLang(zh ? "en" : "zh")} className="ml-auto inline-flex items-center gap-1 text-xs rounded-full border border-white/30 px-3 py-1 hover:bg-white/10"><Globe className="h-3.5 w-3.5" />{zh ? "English" : "中文"}</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 pt-8 pb-32">
        <article className="legal-article bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 text-[14px] leading-relaxed space-y-5 [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:text-slate-700 [&_li]:text-slate-700">
          <p className="text-xs text-slate-400">{zh ? "最后更新" : "Last updated"}: {COMPANY.updated}</p>
          {typeof children === "function" ? children(zh, co) : children}
        </article>
        <footer className="mt-6 text-xs text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="legal-footer">
          <span data-testid="legal-company-legal">{co.legal}</span>
          <span data-testid="legal-company-address">{co.address}</span>
          <a href={`tel:${co.phone.replace(/\D/g, "")}`} className="hover:text-slate-800" data-testid="legal-company-phone">{co.phone}</a>
          <a href={`mailto:${co.email}`} className="hover:text-slate-800" data-testid="legal-company-email">{co.email}</a>
          <a href={`mailto:${co.privacyEmail}`} className="hover:text-slate-800" data-testid="legal-privacy-email">{zh ? "隐私 / 支持：" : "Privacy / support: "}{co.privacyEmail}</a>
          <span className="ml-auto inline-flex gap-3">
            <Link to="/sms-terms" data-testid="legal-link-sms-terms" className="inline-flex items-center gap-1 hover:text-slate-800"><MessageSquareText className="h-3.5 w-3.5" />{zh ? "短信条款" : "SMS Terms"}</Link>
            <Link to="/privacy" data-testid="legal-link-privacy" className="inline-flex items-center gap-1 hover:text-slate-800"><ShieldCheck className="h-3.5 w-3.5" />{zh ? "隐私政策" : "Privacy Policy"}</Link>
            <Link to="/terms" data-testid="legal-link-terms" className="inline-flex items-center gap-1 hover:text-slate-800"><FileText className="h-3.5 w-3.5" />{zh ? "用户协议" : "Terms of Service"}</Link>
          </span>
        </footer>
      </main>

      {/* 底部悬浮出口：公开法务页没有导航栏，给访客一个明确的「我看完了」动作 */}
      <div className="fixed inset-x-0 bottom-0 z-40 pointer-events-none bg-gradient-to-t from-[#f6f5f2] via-[#f6f5f2] to-transparent pt-10"
        data-testid="legal-got-it-bar" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}>
        <div className="max-w-3xl mx-auto px-5 flex justify-center">
          <button type="button" onClick={dismiss} data-testid="legal-got-it"
            className="pointer-events-auto inline-flex items-center gap-2 h-11 px-8 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-lg shadow-slate-900/20 hover:bg-slate-800 active:scale-[0.98] transition">
            <Check className="h-4 w-4" />{zh ? "我知道了" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}
